import { useRef, useState } from 'react';

import { Loader2, Upload, X } from 'lucide-react';

import { Button } from '../../../../components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '../../../../components/ui/dialog';
import { Label } from '../../../../components/ui/label';
import { Switch } from '../../../../components/ui/switch';
import { cn } from '../../../../lib/utils';
import {
  useUploadProjectFilesMutation,
  useUploadProjectMultimediaMutation,
  useUploadProjectQuotesMutation,
} from '../../api/projects';
import { batchSuggestNames } from '../../lib/aiNaming';
import { compressMedia, humanReadableSize } from '../../lib/compressMedia';
import {
  applyCompressResult,
  buildFinalName,
  createInitialRow,
  hasDuplicateNames,
  revokeRowThumbnails,
  updateRowById,
  type UploadRow,
} from '../../lib/uploadRows';
import type { ProjectUploadResponse } from '../../model/project';
import { useSuggestImageNamesMutation } from '../../services/projects.api';
import UploadDropzone from './UploadDropzone';
import UploadPreviewList from './UploadPreviewList';
import { formatUploadFeedback, MAX_LEGACY_QUOTE_SIZE, uploadConfigs, type UploadKind } from './uploadConfig';

interface UploadDialogProps {
  kind: UploadKind | null;
  onOpenChange: (open: boolean) => void;
  onUploaded: (response: ProjectUploadResponse, variant: 'success' | 'error', message: string) => void;
  projectId: number;
}

interface PipelineProgress {
  compressDone: number;
  compressTotal: number;
  namingDone: number;
  namingTotal: number;
}

const ProgressBar = ({ done, label, total }: { done: number; label: string; total: number }) => (
  <div className="flex items-center gap-2">
    <span className="w-28 shrink-0 text-xs text-text-muted">{label}</span>
    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-bg-surface-raised">
      <div
        className="h-full bg-brand-vivid transition-all duration-200"
        style={{ width: total > 0 ? `${(done / total) * 100}%` : '0%' }}
      />
    </div>
    <span className="w-10 shrink-0 text-right text-xs text-text-muted">
      {done}/{total}
    </span>
  </div>
);

const UploadDialog = ({ kind, onOpenChange, onUploaded, projectId }: UploadDialogProps) => {
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<PipelineProgress | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [useAiNaming, setUseAiNaming] = useState(false);
  const useAiNamingRef = useRef(false);

  const [uploadProjectMultimedia, { isLoading: isUploadingImages }] = useUploadProjectMultimediaMutation();
  const [uploadProjectQuotes, { isLoading: isUploadingQuotes }] = useUploadProjectQuotesMutation();
  const [uploadProjectFiles, { isLoading: isUploadingFiles }] = useUploadProjectFilesMutation();
  const [suggestImageNames] = useSuggestImageNamesMutation();

  const isUploading = isUploadingImages || isUploadingQuotes || isUploadingFiles;
  const isBusy = isProcessing || isUploading;
  const config = kind ? uploadConfigs[kind] : null;

  const readyRows = rows.filter((r) => r.status === 'ready');
  const hasOverLimit = rows.some((r) => r.status === 'over_limit');
  const hasError = rows.some((r) => r.status === 'error');
  const duplicates = hasDuplicateNames(rows);

  const canUpload =
    readyRows.length > 0 &&
    !isBusy &&
    !hasOverLimit &&
    !hasError &&
    !duplicates &&
    rows.every((r) => r.status === 'ready');

  const resetState = () => {
    revokeRowThumbnails(rows);
    setRows([]);
    setProgress(null);
    setIsProcessing(false);
    setInputKey((k) => k + 1);
    setUseAiNaming(false);
    useAiNamingRef.current = false;
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && isBusy) return;
    if (!open) resetState();
    onOpenChange(open);
  };

  const runPipeline = async (initialRows: UploadRow[], shouldUseAiNaming: boolean) => {
    setIsProcessing(true);
    setProgress({
      compressDone: 0,
      compressTotal: initialRows.length,
      namingDone: 0,
      namingTotal: 0,
    });

    const pipeline: UploadRow[] = initialRows.map((r) => ({ ...r, status: 'compressing' as const }));
    setRows([...pipeline]);

    const customLimit = kind === 'quotes' ? MAX_LEGACY_QUOTE_SIZE : undefined;

    await Promise.all(
      pipeline.map(async (rowSnap, idx) => {
        try {
          const result = await compressMedia(rowSnap.originalFile, customLimit);
          const update = applyCompressResult(rowSnap, result);
          pipeline[idx] = {
            ...rowSnap,
            ...update,
            finalName: buildFinalName({ ...rowSnap, ...update }),
          };
        } catch {
          pipeline[idx] = { ...rowSnap, status: 'error', errorMessage: 'Error al comprimir' };
        }
        setProgress((prev) => (prev ? { ...prev, compressDone: prev.compressDone + 1 } : prev));
        setRows([...pipeline]);
      })
    );

    if (kind === 'images' && shouldUseAiNaming) {
      const imageRowsForNaming = pipeline.filter((r) => r.status === 'naming' && r.namingFile);

      if (imageRowsForNaming.length > 0) {
        setProgress((prev) => (prev ? { ...prev, namingTotal: imageRowsForNaming.length } : prev));

        const suggestions = await batchSuggestNames(
          async (args) => {
            const result = await suggestImageNames(args).unwrap();
            return result;
          },
          projectId,
          imageRowsForNaming.map((r) => ({
            clientId: r.clientId,
            namingFile: r.namingFile!,
            originalName: r.originalName,
          })),
          (done) => setProgress((prev) => (prev ? { ...prev, namingDone: done } : prev))
        );

        suggestions.forEach(({ clientId, suggestedName }) => {
          const idx = pipeline.findIndex((r) => r.clientId === clientId);
          if (idx === -1) return;
          pipeline[idx] = {
            ...pipeline[idx],
            suggestedName,
            status: 'ready',
            finalName: buildFinalName({ ...pipeline[idx], suggestedName }),
          };
        });

        setRows([...pipeline]);
      }
    }

    pipeline.forEach((r, idx) => {
      if (r.status === 'naming') {
        pipeline[idx] = { ...r, status: 'ready', finalName: buildFinalName(r) };
      }
    });
    setRows([...pipeline]);
    setIsProcessing(false);
  };

  const handleFilesSelected = (files: File[]) => {
    revokeRowThumbnails(rows);
    const newRows = files.map(createInitialRow);
    setRows(newRows);
    void runPipeline(newRows, useAiNamingRef.current);
  };

  const handleAiNamingChange = (checked: boolean): void => {
    useAiNamingRef.current = checked;
    setUseAiNaming(checked);
  };

  const handleEditName = (clientId: string, value: string) => {
    setRows((prev) => updateRowById(prev, clientId, { editedName: value || null }));
  };

  const handleRemoveRow = (clientId: string) => {
    setRows((prev) => {
      const removed = prev.find((r) => r.clientId === clientId);
      if (removed?.thumbnailUrl) URL.revokeObjectURL(removed.thumbnailUrl);
      return prev.filter((r) => r.clientId !== clientId);
    });
  };

  const handleApplyAllSuggestions = () => {
    setRows((prev) =>
      prev.map((r) => {
        if (!r.suggestedName) return r;
        return { ...r, editedName: null, finalName: r.suggestedName };
      })
    );
  };

  const handleRevertAll = () => {
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        editedName: null,
        finalName: buildFinalName({ ...r, editedName: null }),
      }))
    );
  };

  const handleUpload = async () => {
    if (!kind || !canUpload) return;

    const filesToSend = readyRows.map((r) => {
      const src = r.uploadFile ?? r.originalFile;
      return new File([src], r.finalName, { type: src.type });
    });

    try {
      const uploadByKind = {
        images: uploadProjectMultimedia,
        quotes: uploadProjectQuotes,
        files: uploadProjectFiles,
      };
      const response = await uploadByKind[kind]({ projectId, files: filesToSend }).unwrap();
      const message = formatUploadFeedback(response);
      onUploaded(response, response.failed > 0 ? 'error' : 'success', message);
      resetState();
      if (response.failed === 0) onOpenChange(false);
    } catch {
      onUploaded(
        { results: [], successful: 0, failed: 1 },
        'error',
        'No se pudo completar la subida. Intenta nuevamente.'
      );
    }
  };

  if (!config) return null;

  const showProgress = isProcessing && progress !== null;
  const showList = rows.length > 0;
  const showAiNamingToggle = kind === 'images';
  const totalSize = readyRows.reduce((sum, r) => sum + (r.finalSize ?? r.originalSize), 0);
  const uploadLabel = canUpload
    ? `Subir ${readyRows.length} ${readyRows.length === 1 ? 'archivo' : 'archivos'}`
    : config.actionLabel;

  return (
    <Dialog open={!!kind} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        className={cn(
          'flex max-h-[calc(100dvh-(--spacing(4)))] w-[calc(100%-1rem)] max-w-[calc(100%-1rem)]',
          'flex-col overflow-hidden rounded-2xl p-0 sm:max-w-[600px]'
        )}
      >
        <DialogHeader
          className={cn(
            'flex shrink-0 flex-row items-center gap-2 border-b border-border-subtle px-4 py-4',
            'sm:px-6 sm:py-5'
          )}
        >
          <span className={cn('flex size-9 items-center justify-center rounded-xl', config.iconClassName)}>
            <Upload className="size-5" />
          </span>
          <DialogTitle className="min-w-0 truncate font-display text-base font-bold text-brand-navy">
            {config.title}
          </DialogTitle>
          <DialogClose asChild>
            <Button className="ml-auto" disabled={isBusy} size="icon-sm" type="button" variant="ghost">
              <X className="size-4" />
              <span className="sr-only">Cerrar</span>
            </Button>
          </DialogClose>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-4 p-4 sm:p-6">
            {showAiNamingToggle && (
              <div className="flex items-start justify-between gap-4 rounded-lg border border-border-subtle bg-bg-surface px-4 py-3">
                <div className="min-w-0">
                  <Label className="text-sm font-semibold text-text-default" htmlFor="images-ai-naming">
                    Renombrar imágenes con IA
                  </Label>
                  <p className="mt-1 text-xs text-text-muted" id="images-ai-naming-help">
                    Decide antes de seleccionar archivos: activado pide nombres sugeridos por IA; desactivado conserva
                    el nombre original sanitizado.
                  </p>
                  {showList && (
                    <p className="mt-1 text-xs font-medium text-text-subtle">
                      Para cambiar esta opción, quita los archivos seleccionados y vuelve a elegirlos.
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-xs font-semibold text-text-muted">
                    {useAiNaming ? 'Activado' : 'Desactivado'}
                  </span>
                  <Switch
                    id="images-ai-naming"
                    aria-describedby="images-ai-naming-help"
                    checked={useAiNaming}
                    disabled={isBusy || showList}
                    onCheckedChange={handleAiNamingChange}
                  />
                </div>
              </div>
            )}

            {showProgress && (
              <div className="flex flex-col gap-2">
                {progress.compressTotal > 0 && (
                  <ProgressBar done={progress.compressDone} label="Comprimiendo" total={progress.compressTotal} />
                )}
                {progress.namingTotal > 0 && (
                  <ProgressBar done={progress.namingDone} label="Analizando con IA" total={progress.namingTotal} />
                )}
              </div>
            )}

            {showList ? (
              <>
                <UploadPreviewList
                  disabled={isBusy}
                  onApplyAllSuggestions={handleApplyAllSuggestions}
                  onEditName={handleEditName}
                  onRemoveRow={handleRemoveRow}
                  onRevertAll={handleRevertAll}
                  rows={rows}
                />
                {!isProcessing && (
                  <button
                    className="text-left w-41 text-xs text-brand-vivid hover:underline"
                    onClick={resetState}
                    type="button"
                  >
                    + Seleccionar otros archivos
                  </button>
                )}
              </>
            ) : (
              <UploadDropzone
                config={config}
                disabled={isBusy}
                inputKey={inputKey}
                onFilesSelected={handleFilesSelected}
              />
            )}
          </div>
        </div>

        <div
          className={cn(
            'flex shrink-0 flex-col gap-3 border-t border-border-subtle px-4 py-3',
            'sm:flex-row sm:items-center sm:justify-between sm:px-6'
          )}
        >
          <span className="min-h-4 text-xs text-text-muted">
            {canUpload && totalSize > 0 ? `Total: ${humanReadableSize(totalSize)}` : null}
          </span>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button disabled={isBusy} onClick={() => handleOpenChange(false)} type="button" variant="outline">
              Cancelar
            </Button>
            <Button
              className="bg-brand-vivid text-white hover:bg-brand-sky-dark"
              disabled={!canUpload}
              onClick={() => {
                void handleUpload();
              }}
              type="button"
            >
              {isUploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
              {isUploading ? 'Subiendo…' : uploadLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UploadDialog;
